/**
 * What buyers type, how often, which way it is moving, and what on the board
 * it is about.
 *
 * Two sources joined on the term: Google Ads (volume, growth, competition,
 * top-of-page bid) and Google Trends (interest now against a year ago, the
 * rising queries). Read from the LANDED LOOKUPS, not from the analyst's
 * evidence: a lookup returns thirty terms and the related ones Google offers,
 * and the analyst repeats a handful. This is where the rest are.
 *
 * Two views of one list. BY SUBJECT answers "how big is the idea / the type",
 * adding up the seeded terms; the TERMS table answers "which words, exactly",
 * with Google's related terms below the seeded ones, dimmed, because they were
 * offered rather than asked for.
 *
 * Two marks, one hue. A volume bar beside each number, scaled to the largest
 * in its list, so the sizes read at a glance; and a sparkline of the Trends
 * series beside the year-ago → now figures, so a spike that faded and a steady
 * climb stop looking like the same "+61%". Neither is the only place a value
 * lives: the numbers stay as text and the table is the table.
 */
import { useState } from 'react';
import type { BoardroomDemand, BoardroomDemandSubject, BoardroomDemandTerm } from '../../../types';
import { VerdictBadge } from '../ItemCard';
import { KIND_LABELS } from '../kinds';
import MissingList, { type OnDig } from './MissingList';
import { VolumeBar } from './Sparkline';
import { DemandTermsTable, pct, pctClass } from './LookupPanels';
import { useReportSeen } from '../useSeen';
import NewHere from './NewHere';

/** NOBODY SEARCHES FOR IT: Google Ads says 0 a month and there is no Trends
 *  line to say otherwise. More than half the terms on a big room, so they sit
 *  behind a link instead of pushing the ones people search for down. A term
 *  Ads has no number for (null) is unknown, not zero, and stays. Pure. */
export function isZeroTerm(t: BoardroomDemandTerm): boolean {
  return t.volume === 0 && !t.trend;
}

export function isZeroSubject(s: BoardroomDemandSubject): boolean {
  return s.volume === 0 && s.trend_change === null;
}

/** The link that shows or hides the zero rows of one list. */
function ZeroToggle({ count, noun, shown, onToggle }: { count: number; noun: string; shown: boolean; onToggle: () => void }) {
  if (count === 0) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mt-1 text-xs text-gray-500 underline hover:no-underline dark:text-gray-400"
      data-testid={`demand-zero-${noun}`}
    >
      {shown ? `Hide the ${count} ${noun} with 0 searches a month` : `Show ${count} ${noun} with 0 searches a month`}
    </button>
  );
}

export default function DemandReport({
  data,
  onOpen,
  onDig,
  digging,
  unseen,
  onSeen,
  roomName,
}: {
  data: BoardroomDemand;
  onOpen: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  unseen?: ReadonlySet<number>;
  onSeen?: (ids: number[]) => void;
  roomName?: string;
}) {
  const empty = data.totals.terms === 0 && data.subjects.length === 0;
  const [showZero, setShowZero] = useState(false);
  const toggleZero = () => setShowZero(v => !v);
  const zeroTerms = data.terms.filter(isZeroTerm).length;
  const zeroSubjects = data.subjects.filter(isZeroSubject).length;
  const terms = showZero ? data.terms : data.terms.filter(t => !isZeroTerm(t));
  const subjects = showZero ? data.subjects : data.subjects.filter(s => !isZeroSubject(s));
  const related = data.totals.terms - data.totals.seeded;
  const maxSubject = Math.max(0, ...data.subjects.map(s => s.volume));
  // WHICH WAY THE MARKET IS MOVING, in one line. Every term already carries
  // its growth and its top bid, but reading the direction meant scanning the
  // column: one room was seven terms shrinking against three growing, which
  // nothing said out loud.
  const moves = data.terms.map(t => t.growth).filter((g): g is number => g != null);
  const growing = moves.filter(g => g > 0).length;
  const shrinking = moves.filter(g => g < 0).length;
  const topBid = Math.max(0, ...data.terms.map(t => t.top_bid ?? 0));
  // ONE TRACKER. This report's rows are KEYWORDS, which no item id names, so
  // only the questions underneath carry marks — but a tracker each still meant
  // the terms table pruned the questions' marks on sight.
  const seen = useReportSeen(unseen, onSeen, data.missing.map(m => m.item_id));
  return (
    <div className="flex flex-col gap-3 min-h-0">
      <NewHere count={seen.waitingHere} glowing={seen.glowing} onJump={seen.next} onClearAll={seen.readAll} />
      {!empty && (
        <div className="flex items-center gap-2 flex-wrap text-xs text-gray-400">
          {data.totals.seeded} term{data.totals.seeded === 1 ? '' : 's'} asked for
          {related > 0 && ` · ${related} Google offered`}
          {' · '}{data.totals.with_trend} with a trend line · {Math.round(data.totals.volume).toLocaleString()} searches a month across what was asked
          {(growing > 0 || shrinking > 0) && ` · ${growing} growing, ${shrinking} shrinking`}
          {topBid > 0 && ` · top bid $${topBid.toFixed(2)}`}
          {data.totals.unasked > 0 && ` · ${data.totals.unasked} thing${data.totals.unasked === 1 ? '' : 's'} nobody asked about`}
        </div>
      )}

      {data.subjects.length > 0 && (
        <section>
          <h3 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">By subject</h3>
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {subjects.map(s => (
              <li key={s.id} className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-2.5" data-testid={`demand-subject-${s.id}`}>
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <button type="button" onClick={() => onOpen(s.id)} className="text-sm font-semibold text-gray-800 dark:text-gray-100 hover:underline text-left">
                    {s.title}
                  </button>
                  <span className="text-[11px] text-gray-400">{KIND_LABELS[s.kind]}</span>
                  {s.verdict && <VerdictBadge verdict={s.verdict} />}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <VolumeBar value={s.volume} max={maxSubject} width={80} testId={`bar-subject-${s.id}`} />
                  <span className="font-mono text-gray-800 dark:text-gray-100">{Math.round(s.volume).toLocaleString()}/mo</span>
                  {s.best_growth !== null && (
                    <span className={`font-mono text-xs ${pctClass(s.best_growth)}`} title="best growth among its terms (Google Ads)">
                      ads {pct(s.best_growth)}
                    </span>
                  )}
                  {s.trend_change !== null && (
                    <span className={`font-mono text-xs ${pctClass(s.trend_change)}`} title="interest now against a year ago (Google Trends)">
                      trends {pct(s.trend_change)}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-[11px] text-gray-400 truncate" title={s.terms.join(', ')}>
                  {s.terms.slice(0, 4).join(' · ')}{s.terms.length > 4 ? ` · +${s.terms.length - 4}` : ''}
                </div>
              </li>
            ))}
          </ul>
          <ZeroToggle count={zeroSubjects} noun="subjects" shown={showZero} onToggle={toggleZero} />
        </section>
      )}

      {data.terms.length > 0 && (
        <section>
          <h3 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
            Terms{related > 0 && <span className="normal-case tracking-normal"> · the {related} Google offered as related are dimmed below the ones asked for</span>}
          </h3>
          <DemandTermsTable
            terms={terms}
            onOpen={onOpen}
            room={roomName}
            seen={seen}
          />
          <ZeroToggle count={zeroTerms} noun="terms" shown={showZero} onToggle={toggleZero} />
          {data.truncated && <div className="mt-1 text-[11px] text-gray-400">The list is capped; narrow the lens to see the rest.</div>}
        </section>
      )}

      <MissingList missing={data.missing} empty={empty} onOpen={onOpen} onDig={onDig} digging={digging} seen={seen} />
    </div>
  );
}
